/**
 * Kill rewards: hero XP and gold for every enemy the party kills, plus the smaller end-of-run
 * completion and timed bonuses (PLAN §11 loop step 1). Pure and deterministic.
 *
 * Tuning (Venom Altar full route at +2: 41 trash, 1 miniboss, 3 bosses):
 *   - kills = 80 XP and ≈ 420 gold. The old flat run XP was 120 at a timed +2, so kills are
 *     ~67% of it; the completion + timed bonuses bring a timed +2 back to ~120 XP.
 *   - Everything scales with rewardKeyMult(L) = (10 + L) / 12 (1.0 at +2, 2.0 at +14), which
 *     keeps a timed run at ~100 + 10·L XP, the same pacing as before (hero 30 around run 60–70).
 *   - Trash pays by forces (a 25-forces big mob pays ~4× fodder). Bosses pay the most, and the
 *     dungeon's final boss pays ×1.5.
 *   - Non-boss NPCs worth 0 forces (summoned adds, split spawns) pay nothing, so add-spawning
 *     fights can't be farmed.
 *
 * Rounding: killReward is exact (fractional). killRewardsFromEvents rounds with a running carry
 * (entry = round(cumulative) − round(previous cumulative)), so every pop is a whole number, the
 * run total is round(exact total), and the total grows smoothly with key level instead of
 * jumping when 40 one-XP trash kills all round up at once. Compute a run's total from its whole
 * event list (per-tick slices re-round each slice).
 */
import type { ContentRegistry, DungeonDef, Ms, NpcDef, NpcId, RunResult, SimEvent, UnitId, Vec2, World } from '@keystone/sim';
import { getContentBundle } from '@keystone/content';

export interface KillReward {
  xp: number;
  gold: number;
}

/** One rewarded kill (the web client pops "+XP / +gold" text at `pos`). */
export interface KillRewardEntry extends KillReward {
  unitId: UnitId;
  npcId: NpcId;
  /** Where the unit died (its position in the world passed in). */
  pos: Vec2;
  /** Sim time of the death event. */
  time: Ms;
  boss: boolean;
  finalBoss: boolean;
}

export interface KillRewardSummary extends KillReward {
  entries: KillRewardEntry[];
}

/** Reward multiplier for a key level: 1.0 at +2, +1/12 per level. */
export function rewardKeyMult(keyLevel: number): number {
  return (10 + Math.max(2, keyLevel)) / 12;
}

/** Trash XP at +2: base + per enemy-forces point. */
export const TRASH_XP_BASE = 0.6;
export const TRASH_XP_PER_FORCES = 0.08;
/** Trash gold: base + per forces point. */
export const TRASH_GOLD_BASE = 1;
export const TRASH_GOLD_PER_FORCES = 0.5;
/** Boss XP / gold at +2. */
export const BOSS_XP = 9;
export const BOSS_GOLD = 60;
/** The dungeon's last boss on the route pays this much more. */
export const FINAL_BOSS_MULT = 1.5;

export interface KillRewardOptions {
  /** This boss is the dungeon's final boss (FINAL_BOSS_MULT). */
  finalBoss?: boolean;
}

/**
 * Exact (unrounded) XP and gold for killing one `npc` in a +`keyLevel` key. Round for display
 * (Math.round); killRewardsFromEvents rounds a whole run without drift.
 */
export function killReward(npc: Pick<NpcDef, 'tier' | 'forces'>, keyLevel: number, opts: KillRewardOptions = {}): KillReward {
  const m = rewardKeyMult(keyLevel);
  if (npc.tier === 'boss') {
    const f = opts.finalBoss ? FINAL_BOSS_MULT : 1;
    return { xp: BOSS_XP * f * m, gold: BOSS_GOLD * f * m };
  }
  if (npc.forces <= 0) return { xp: 0, gold: 0 };
  return { xp: (TRASH_XP_BASE + npc.forces * TRASH_XP_PER_FORCES) * m, gold: (TRASH_GOLD_BASE + npc.forces * TRASH_GOLD_PER_FORCES) * m };
}

/** NPC ids of the final boss: the bosses of the last encounter pack on the route. */
export function finalBossNpcIds(dungeon: DungeonDef, content: Pick<ContentRegistry, 'encounter'>): Set<NpcId> {
  for (let i = dungeon.route.length - 1; i >= 0; i--) {
    const pack = dungeon.packs.find((p) => p.id === dungeon.route[i]);
    if (!pack?.encounterId) continue;
    const enc = content.encounter(pack.encounterId);
    return new Set(enc?.bossNpcIds ?? pack.spawns.map((s) => s.npcId));
  }
  return new Set();
}

/**
 * Sum the rewards of a run's enemy deaths. `events` is the run's event stream (or any slice of
 * it, e.g. one tick's events for live "+XP" pops); `world` resolves unit ids to NPCs and death
 * positions. Each unit pays at most once; party deaths, unknown units and zero-reward adds are
 * skipped. Entries come out in event order.
 */
export function killRewardsFromEvents(
  events: readonly SimEvent[],
  world: Readonly<Pick<World, 'units' | 'dungeonId'>>,
  content: Pick<ContentRegistry, 'npc' | 'encounter' | 'dungeon'>,
  keyLevel: number,
): KillRewardSummary {
  const dungeon = content.dungeon(world.dungeonId);
  const finals = dungeon ? finalBossNpcIds(dungeon, content) : new Set<NpcId>();
  const seen = new Set<UnitId>();
  const entries: KillRewardEntry[] = [];
  let exactXp = 0;
  let exactGold = 0;
  let xp = 0;
  let gold = 0;
  for (const e of events) {
    if (e.type !== 'death') continue;
    const id = e.targetID;
    if (seen.has(id)) continue;
    const u = world.units[id];
    if (!u || u.faction !== 'enemy' || !u.npcId) continue;
    const npc = content.npc(u.npcId);
    if (!npc) continue;
    seen.add(id);
    const finalBoss = npc.tier === 'boss' && finals.has(npc.id);
    const r = killReward(npc, keyLevel, { finalBoss });
    if (r.xp <= 0 && r.gold <= 0) continue;
    exactXp += r.xp;
    exactGold += r.gold;
    const dx = roundCum(exactXp) - xp;
    const dg = roundCum(exactGold) - gold;
    xp += dx;
    gold += dg;
    entries.push({ unitId: id, npcId: npc.id, pos: { x: u.pos.x, y: u.pos.y }, time: e.timestamp, boss: npc.tier === 'boss', finalBoss, xp: dx, gold: dg });
  }
  return { entries, xp, gold };
}

/**
 * Best guess at a run's kill rewards from its RunResult alone (for callers that did not track
 * death events, and for old run records). Walks the base dungeon route in order, counting trash
 * until the run's forces are covered; a completed run counts every required boss.
 */
export function estimateKillRewards(result: Pick<RunResult, 'dungeonId' | 'level' | 'forces' | 'completed'>): KillReward {
  const bundle = getContentBundle();
  const dungeon = bundle.dungeons.find((d) => d.id === result.dungeonId);
  if (!dungeon) return { xp: 0, gold: 0 };
  const npc = (id: NpcId) => bundle.npcs.find((n) => n.id === id);
  const finals = finalBossNpcIds(dungeon, { encounter: (id) => bundle.encounters.find((e) => e.id === id) });
  let forces = 0;
  let xp = 0;
  let gold = 0;
  for (const packId of dungeon.route) {
    const pack = dungeon.packs.find((p) => p.id === packId);
    if (!pack) continue;
    // Packs before the run's forces ran out were killed; a completed run also killed every
    // required boss (even one routed after the last trash).
    const reached = forces < result.forces;
    const requiredBoss = !!pack.encounterId && result.completed && dungeon.requiredEncounters.includes(pack.encounterId);
    if (!reached && !requiredBoss) continue;
    for (const s of pack.spawns) {
      const n = npc(s.npcId);
      if (!n) continue;
      const r = killReward(n, result.level, { finalBoss: n.tier === 'boss' && finals.has(n.id) });
      xp += r.xp;
      gold += r.gold;
      forces += n.tier === 'boss' ? 0 : n.forces;
    }
  }
  return { xp: roundCum(xp), gold: roundCum(gold) };
}

/** Round a running total (tiny epsilon so 0.5 sums that float to 0.4999… still round up). */
const roundCum = (x: number): number => Math.round(x + 1e-9);

// ============================================================================
// End-of-run bonuses
// ============================================================================

/** XP for finishing the dungeon (timed or depleted). */
export function completionXp(level: number): number {
  return Math.round(12 + 1.5 * level);
}
/** Extra XP for beating the timer. */
export function timedXp(level: number): number {
  return Math.round(20 + 1.5 * level);
}
/** Gold for finishing the dungeon (timed or depleted). */
export function completionGold(level: number): number {
  return 50 + 5 * level;
}
/** Extra gold for beating the timer (+25 per chest above 1). */
export function timedGold(level: number, chest: 0 | 1 | 2 | 3): number {
  return 40 + 5 * level + 25 * Math.max(0, chest - 1);
}

export interface RunRewards {
  /** Kill rewards (from killRewardsFromEvents, or estimated). */
  killXp: number;
  killGold: number;
  completionXp: number;
  completionGold: number;
  timedXp: number;
  timedGold: number;
  /** Totals: XP per party member, gold to the shared purse. */
  xp: number;
  gold: number;
  /** True when kill rewards were estimated from the RunResult. */
  estimated: boolean;
}

/**
 * Everything a run pays. Kills always pay (even on a depleted or abandoned run); finishing adds
 * the completion bonus; beating the timer adds the timed bonus.
 */
export function runRewards(result: Pick<RunResult, 'dungeonId' | 'level' | 'forces' | 'completed' | 'timed' | 'chest'>, kills?: KillReward): RunRewards {
  const k = kills ? { xp: clampReward(kills.xp), gold: clampReward(kills.gold) } : estimateKillRewards(result);
  const cXp = result.completed ? completionXp(result.level) : 0;
  const cGold = result.completed ? completionGold(result.level) : 0;
  const timed = result.completed && result.timed;
  const tXp = timed ? timedXp(result.level) : 0;
  const tGold = timed ? timedGold(result.level, result.chest) : 0;
  return {
    killXp: k.xp,
    killGold: k.gold,
    completionXp: cXp,
    completionGold: cGold,
    timedXp: tXp,
    timedGold: tGold,
    xp: k.xp + cXp + tXp,
    gold: k.gold + cGold + tGold,
    estimated: !kills,
  };
}

const clampReward = (n: number): number => (Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0);
